// Sección "Programas de tratamiento" en la ficha del paciente (client-detail-drawer).

import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import { ClipboardList, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { useConfirm } from "@/hooks/useConfirm";

type AssignmentSession = {
  serviceId: string;
  serviceName: string;
  quantity: number;
  used: number;
  remaining: number;
};

type Assignment = {
  id: string;
  status: "active" | "completed" | "expired";
  startDate: string;
  expiresAt: string;
  program: { id: string; name: string; description: string | null; durationMonths: number };
  sessions: AssignmentSession[];
};

type AvailableProgram = { id: string; name: string; durationMonths: number; archived: boolean };

function profQs(effectiveProfessionalId: string | null): string {
  return effectiveProfessionalId ? `?professionalId=${encodeURIComponent(effectiveProfessionalId)}` : "";
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function statusLabel(status: Assignment["status"]): { label: string; bg: string; color: string } {
  switch (status) {
    case "active":
      return { label: "Activo", bg: "#dcfce7", color: "#15803d" };
    case "completed":
      return { label: "Completado", bg: "#dbeafe", color: "#1d4ed8" };
    default:
      return { label: "Vencido", bg: "#f1f5f9", color: "#64748b" };
  }
}

export function PatientProgramsSection({
  clientId,
  effectiveProfessionalId,
}: {
  clientId: string;
  effectiveProfessionalId: string | null;
}) {
  const { refresh } = useAuth();
  const confirm = useConfirm();
  const pq = profQs(effectiveProfessionalId);
  const [assignments, setAssignments] = useState<Assignment[] | null>(null);
  const [picking, setPicking] = useState(false);
  const [available, setAvailable] = useState<AvailableProgram[] | null>(null);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients/${clientId}/programs${pq}`,
      { credentials: "include" },
      refresh,
    );
    if (!r.ok) {
      setAssignments([]);
      return;
    }
    setAssignments((await r.json()) as Assignment[]);
  }

  useEffect(() => {
    setAssignments(null);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, effectiveProfessionalId]);

  async function openPicker() {
    setErr(null);
    setPicking(true);
    if (available == null) {
      const r = await fetchWithSessionSync(
        `/api/dashboard/programs${pq}`,
        { credentials: "include" },
        refresh,
      );
      if (r.ok) {
        const list = (await r.json()) as AvailableProgram[];
        setAvailable(list.filter((p) => !p.archived));
      } else {
        setAvailable([]);
      }
    }
  }

  async function assign() {
    if (!selectedProgramId) {
      setErr("Elegí un programa.");
      return;
    }
    setBusy(true);
    setErr(null);
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients/${clientId}/programs${pq}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ programId: selectedProgramId }),
      },
      refresh,
    );
    setBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setErr(j.error ?? "No se pudo asignar el programa.");
      return;
    }
    setPicking(false);
    setSelectedProgramId("");
    await load();
  }

  async function cancelAssignment(a: Assignment) {
    if (
      !(await confirm({
        message: `¿Cancelar el programa "${a.program.name}" para este paciente?`,
        tone: "danger",
        confirmLabel: "Cancelar programa",
      }))
    )
      return;
    await fetchWithSessionSync(
      `/api/dashboard/clients/${clientId}/programs/${a.id}${pq}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: "expired" }),
      },
      refresh,
    );
    await load();
  }

  if (assignments === null) {
    return (
      <div
        className={css({
          h: "16",
          rounded: "lg",
          bg: "#f1f5f9",
          animation: "pulse 1.5s ease-in-out infinite",
        })}
      />
    );
  }

  const activeAssignments = assignments.filter((a) => a.status === "active");

  return (
    <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
      {activeAssignments.length === 0 ? (
        <div
          className={css({
            p: "4",
            rounded: "xl",
            bg: "#f8fafc",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            display: "flex",
            gap: "3",
            alignItems: "flex-start",
          })}
        >
          <div
            className={css({
              w: "9",
              h: "9",
              rounded: "lg",
              bg: "#f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            })}
          >
            <ClipboardList size={18} strokeWidth={1.5} className={css({ color: "#94a3b8" })} />
          </div>
          <div className={css({ flex: "1", minW: 0 })}>
            <p className={css({ fontSize: "sm", fontWeight: "600", color: "#334155", mb: "1" })}>
              Sin programa activo
            </p>
            <p className={css({ fontSize: "sm", color: "#94a3b8", mb: "3" })}>
              Asigná un programa de tratamiento para habilitar sus servicios exclusivos.
            </p>
            <Button variant="secondary" onClick={() => void openPicker()}>
              Asignar programa
            </Button>
          </div>
        </div>
      ) : (
        <>
          {activeAssignments.map((a) => {
            const sc = statusLabel(a.status);
            return (
              <div
                key={a.id}
                className={css({
                  p: "4",
                  rounded: "xl",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  bg: "white",
                })}
              >
                <div className={css({ display: "flex", justifyContent: "space-between", gap: "2", alignItems: "flex-start" })}>
                  <div className={css({ minW: 0 })}>
                    <p className={css({ fontSize: "sm", fontWeight: "700", color: "#0f172a" })}>{a.program.name}</p>
                    <p className={css({ fontSize: "xs", color: "#64748b", mt: "0.5" })}>
                      Desde {formatDate(a.startDate)} · Vence {formatDate(a.expiresAt)}
                    </p>
                  </div>
                  <span
                    className={css({
                      fontSize: "11px",
                      fontWeight: "600",
                      px: "2.5",
                      py: "1",
                      rounded: "full",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    })}
                    style={{ background: sc.bg, color: sc.color }}
                  >
                    {sc.label}
                  </span>
                </div>
                <ul className={css({ display: "flex", flexDir: "column", gap: "1", mt: "3" })}>
                  {a.sessions.map((s) => (
                    <li key={s.serviceId} className={css({ fontSize: "sm", color: "#334155" })}>
                      {s.serviceName}: {s.used}/{s.quantity} usadas · {s.remaining} restante
                      {s.remaining === 1 ? "" : "s"}
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => void cancelAssignment(a)}
                  className={css({
                    mt: "3",
                    fontSize: "xs",
                    fontWeight: "600",
                    color: "#b91c1c",
                    bg: "transparent",
                    border: "none",
                    cursor: "pointer",
                    p: "0",
                    _hover: { opacity: 0.75 },
                  })}
                >
                  Cancelar programa
                </button>
              </div>
            );
          })}
          <Button variant="secondary" onClick={() => void openPicker()} className={css({ alignSelf: "flex-start" })}>
            Asignar otro programa
          </Button>
        </>
      )}

      {picking ? (
        <div
          className={css({
            p: "4",
            rounded: "xl",
            borderWidth: "1px",
            borderColor: "#e2e8f0",
            bg: "#f8fafc",
            display: "flex",
            flexDir: "column",
            gap: "3",
          })}
        >
          <div className={css({ display: "flex", justifyContent: "space-between", alignItems: "center" })}>
            <p className={css({ fontSize: "sm", fontWeight: "600", color: "#334155" })}>Elegí un programa</p>
            <button
              type="button"
              aria-label="Cerrar"
              onClick={() => setPicking(false)}
              className={css({ border: "none", bg: "transparent", cursor: "pointer", color: "#94a3b8" })}
            >
              <X size={16} aria-hidden />
            </button>
          </div>
          {err ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{err}</p> : null}
          {available == null ? (
            <p className={css({ fontSize: "sm", color: "#94a3b8" })}>Cargando…</p>
          ) : available.length === 0 ? (
            <p className={css({ fontSize: "sm", color: "#94a3b8" })}>
              No hay programas disponibles. Creá uno en la sección Programas.
            </p>
          ) : (
            <>
              <select
                value={selectedProgramId}
                onChange={(e) => setSelectedProgramId(e.target.value)}
                className={css({
                  width: "100%",
                  px: "3",
                  py: "2",
                  rounded: "md",
                  borderWidth: "1px",
                  borderColor: "#e2e8f0",
                  bg: "white",
                  color: "#0f172a",
                  fontSize: "sm",
                })}
              >
                <option value="">Seleccioná un programa…</option>
                {available.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} ({p.durationMonths} {p.durationMonths === 1 ? "mes" : "meses"})
                  </option>
                ))}
              </select>
              <Button variant="primary" disabled={busy} onClick={() => void assign()} className={css({ alignSelf: "flex-start" })}>
                {busy ? "Asignando…" : "Asignar"}
              </Button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
